import { useState, useEffect, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import type { ProjectFieldKey } from '@/contexts/FieldConfigContext';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { TextareaWithCount } from '@/components/ui/textarea-with-count';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CalendarIcon, ChevronDown, Plus, X, Minus, Sparkles, Star, Info, AlertTriangle, Briefcase, ArrowRight } from 'lucide-react';
import { format, addDays, addWeeks, addMonths, addYears, differenceInDays, differenceInWeeks, differenceInMonths, differenceInYears } from 'date-fns';
import { cn } from '@/lib/utils';
import { useFieldConfig } from '@/contexts/FieldConfigContext';
import { MultiSelect } from '@/components/ui/multiselect';
import { MultiSelectWithFavorites } from '@/components/ui/multiselect-with-favorites';
import { Combobox } from '@/components/ui/combobox';
import type { DurationUnit } from '@/contexts/FieldConfigContext';
import { fetchClients, fetchEmployees, getDataMode } from '@/lib/data-service';

// Function to create dynamic form validation schema based on mandatory fields
const createProjectFormSchema = (mandatoryFields: ProjectFieldKey[]) => {
  const isMandatory = (field: ProjectFieldKey) => mandatoryFields.includes(field);
  
  return z.object({
    // Always required fields (isAlwaysMandatory)
    projectName: z.string().min(1, 'Project name is required'),
    projectCode: z.string().min(1, 'Project code is required'),
    // Configurable fields - required based on settings
    description: z.string().optional(),
    status: isMandatory('status') 
      ? z.string().min(1, 'Status is required') 
      : z.string().optional(),
    priority: isMandatory('priority') 
      ? z.string().min(1, 'Priority is required') 
      : z.string().optional(),
    type: isMandatory('type') 
      ? z.string().min(1, 'Type is required') 
      : z.string().optional(),
    startDate: isMandatory('startDate') 
      ? z.date({ message: 'Start date is required' }) 
      : z.date().optional(),
    endDate: isMandatory('endDate') 
      ? z.date({ message: 'End date is required' }) 
      : z.date().optional(),
    duration: isMandatory('duration') 
      ? z.number({ message: 'Duration is required' }).min(1, 'Duration must be at least 1') 
      : z.number().optional(),
    durationUnit: z.enum(['days', 'weeks', 'months', 'years']).optional(),
    projectManager: isMandatory('projectManager') 
      ? z.string().min(1, 'Project manager is required') 
      : z.string().optional(),
    clients: isMandatory('clients') 
      ? z.array(z.string()).min(1, 'Client is required') 
      : z.array(z.string()).optional(),
    budget: isMandatory('budget') 
      ? z.string().min(1, 'Budget is required') 
      : z.string().optional(),
    tags: z.array(z.string()).optional(),
    attachments: z.array(z.any()).optional(),
  });
};

// Default schema for type inference (will be overridden with dynamic schema in component)
const projectFormSchema = createProjectFormSchema(['projectName', 'projectCode']);

type ProjectFormValues = z.infer<typeof projectFormSchema>;

// Custom field type
interface CustomField {
  id: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: 'text' | 'number' | 'date' | 'boolean' | 'dropdown' | 'multiselect';
  defaultValue?: string;
  options?: string[];
}

interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
  isFavorite: boolean;
}

interface ProjectFormProps {
  onSubmit: (data: ProjectFormValues & { customFields: CustomField [] }) => void;
  onCancel: () => void;
  onMinimize?: () => void;
  initialData?: Partial<ProjectFormValues>;
  isMinimized?: boolean;
  onProgressChange?: (progress: number) => void;
  onIncompleteFieldsChange?: (fields: string[]) => void;
  quickCreateMode?: boolean;
  onShowFullDetails?: () => void;
  showFullDetails?: boolean;
  // Template and AI support for Killer UX
  templates?: ProjectTemplate[];
  onTemplateSelect?: (templateId: string) => void;
  selectedTemplate?: string;
  onAiAssist?: () => void;
  highlightedField?: string | null;
}

// Mock data for dropdowns
const statusOptions = ['Not Started', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];
const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];
const typeOptions = ['Internal', 'Client Project', 'R&D', 'Maintenance', 'Consulting'];
const projectManagerOptions = ['John Smith', 'Sarah Johnson', 'Michael Chen', 'Emily Davis'];
const clientOptions = [
  'Acme Corp',
  'TechStart Inc',
  'Global Solutions',
  'Innovation Labs',
  'Alpha Industries',
  'Beta Technologies',
  'Gamma Enterprises',
  'Delta Systems',
  'Epsilon Group',
  'Zeta Corporation',
  'Theta Ventures',
  'Iota Partners',
  'Kappa Holdings',
  'Lambda Consulting',
  'Mu Dynamics',
  'Nu Solutions',
  'Xi Innovations',
  'Omicron Tech',
  'Pi Digital',
  'Rho Analytics',
  'Sigma Networks',
  'Tau Services',
  'Upsilon Labs',
  'Phi Enterprises',
  'Chi Systems',
  'Psi Group',
  'Omega Industries',
  'Apex Corporation',
  'Zenith Partners',
  'Vertex Solutions',
  'Summit Technologies',
  'Horizon Ventures',
  'Nexus Group',
  'Quantum Systems',
  'Fusion Enterprises',
  'Catalyst Inc',
  'Momentum Partners',
  'Velocity Corp',
  'Pinnacle Solutions',
  'Vanguard Tech',
];
const tagOptions = ['Urgent', 'Strategic', 'Revenue', 'Innovation', 'Compliance', 'Marketing', 'Sales', 'Operations'];

export function ProjectForm({ 
  onSubmit, 
  onCancel,
  onMinimize,
  initialData, 
  isMinimized, 
  onProgressChange, 
  onIncompleteFieldsChange,
  quickCreateMode = false,
  onShowFullDetails,
  showFullDetails = true,
  templates = [],
  onTemplateSelect,
  selectedTemplate,
  onAiAssist,
  highlightedField,
}: ProjectFormProps) {
  const { mandatoryFields, fieldConfigs, getFieldConfig, timelineSettings, generateProjectCode } = useFieldConfig();
  
  // Create dynamic schema based on mandatory fields from settings
  const dynamicSchema = useMemo(() => {
    return createProjectFormSchema(mandatoryFields);
  }, [mandatoryFields]);
  
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const projectNameInputRef = useRef<HTMLInputElement>(null);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  // Initialize hasFormChanges to true if initialData is provided (form was restored from draft)
  const [hasFormChanges, setHasFormChanges] = useState(!!initialData);
  
  // Refs for field elements to enable scrolling
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  
  // AI form state
  const [aiProjectDescription, setAiProjectDescription] = useState('');
  const [aiObjectives, setAiObjectives] = useState('');
  const [aiTimeline, setAiTimeline] = useState('');
  
  // Date picker popover state
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  
  // Controlled month state for End Date calendar to ensure it shows the selected date when reopened
  const [endDateMonth, setEndDateMonth] = useState<Date | undefined>(undefined);
  
  // Toggle between End Date mode and Duration mode - use settings default
  const [isEndDateMode, setIsEndDateMode] = useState(timelineSettings.defaultToEndDate);
  
  // Track which field was last edited for two-way binding between end date and duration
  const [lastEditedField, setLastEditedField] = useState<'endDate' | 'duration' | null>(null);
  const [showDurationCapWarning, setShowDurationCapWarning] = useState(false);
  
  // Maximum duration values for each unit
  const MAX_DURATION_VALUES: Record<DurationUnit, number> = {
    days: 18250,   // ~50 years
    weeks: 2600,   // ~50 years
    months: 600,   // 50 years
    years: 50      // 50 years
  };

  const getMaxDuration = (unit: DurationUnit): number => {
    return MAX_DURATION_VALUES[unit];
  };

  // Helper functions for duration calculations
  const addDurationToDate = (startDate: Date, value: number, unit: DurationUnit): Date => {
    switch (unit) {
      case 'days':
        return addDays(startDate, value);
      case 'weeks':
        return addWeeks(startDate, value);
      case 'months':
        return addMonths(startDate, value);
      case 'years':
        return addYears(startDate, value);
      default:
        return startDate;
    }
  };

  const calculateDurationInUnit = (startDate: Date, endDate: Date, unit: DurationUnit): number => {
    switch (unit) {
      case 'days':
        return differenceInDays(endDate, startDate);
      case 'weeks':
        return Math.floor(differenceInWeeks(endDate, startDate));
      case 'months':
        return differenceInMonths(endDate, startDate);
      case 'years':
        return differenceInYears(endDate, startDate);
      default:
        return 0;
    }
  };

  const canDisplayInUnit = (startDate: Date, endDate: Date, unit: DurationUnit): boolean => {
    if (!startDate || !endDate) return false;
    try {
      const value = calculateDurationInUnit(startDate, endDate, unit);
      return value >= 1;
    } catch (error) {
      return false;
    }
  };

  const formatDurationUnit = (value: number, unit: DurationUnit): string => {
    const singular = unit.slice(0, -1); // Remove 's' from end
    return value === 1 ? singular : unit;
  };

  const getAvailableUnits = (startDate: Date, endDate: Date): DurationUnit[] => {
    if (!startDate || !endDate) return ['days', 'weeks', 'months', 'years'];
    try {
      const units: DurationUnit[] = ['days', 'weeks', 'months', 'years'];
      const filtered = units.filter(unit => canDisplayInUnit(startDate, endDate, unit));
      return filtered.length > 0 ? filtered : ['days']; // Always return at least 'days'
    } catch (error) {
      return ['days', 'weeks', 'months', 'years'];
    }
  };
  
  // Fetched clients for Live mode
  const [fetchedClients, setFetchedClients] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  
  // Fetched employees for Live mode (for Project Manager dropdown)
  const [fetchedEmployees, setFetchedEmployees] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  
  // Fetch clients and employees on mount
  useEffect(() => {
    const loadClients = async () => {
      setIsLoadingClients(true);
      try {
        const clients = await fetchClients('project-form');
        // Map to consistent format with id and name
        const mappedClients = clients.map((c: any) => ({
          id: c.id,
          name: c.name
        }));
        setFetchedClients(mappedClients);
      } catch (error) {
        console.error('Failed to fetch clients:', error);
      } finally {
        setIsLoadingClients(false);
      }
    };
    
    const loadEmployees = async () => {
      setIsLoadingEmployees(true);
      try {
        const employees = await fetchEmployees('project-form');
        // Map to consistent format with id and name
        const mappedEmployees = employees.map((e: any) => ({
          id: e.id,
          name: e.name
        }));
        setFetchedEmployees(mappedEmployees);
      } catch (error) {
        console.error('Failed to fetch employees:', error);
      } finally {
        setIsLoadingEmployees(false);
      }
    };
    
    loadClients();
    loadEmployees();
  }, []);
  
  // In Quick Create mode, sections start collapsed unless showFullDetails is true
  const [sectionsOpen, setSectionsOpen] = useState({
    general: quickCreateMode ? showFullDetails : true,
    timeline: quickCreateMode ? showFullDetails : true,
    management: quickCreateMode ? showFullDetails : true,
    custom: quickCreateMode ? showFullDetails : true,
  });

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      projectName: initialData?.projectName || '',
      projectCode: initialData?.projectCode || '',
      description: initialData?.description || '',
      status: initialData?.status || '',
      priority: initialData?.priority || '',
      type: initialData?.type || '',
      startDate: initialData?.startDate,
      endDate: initialData?.endDate,
      duration: initialData?.duration,
      durationUnit: (initialData?.durationUnit as DurationUnit) || timelineSettings.defaultDurationUnit,
      projectManager: initialData?.projectManager || '',
      clients: initialData?.clients || [],
      budget: initialData?.budget || '',
      tags: initialData?.tags || [],
      attachments: initialData?.attachments || [],
    },
  });

  // Calculate form completion percentage
  const calculateProgress = () => {
    // Use dynamic mandatory fields from configuration
    const requiredFields = mandatoryFields;

    const values = form.getValues();
    
    // Calculate completed mandatory fields
    const completedMandatoryFields = requiredFields.filter(field => {
      const value = values[field as keyof ProjectFormValues];
      return value !== undefined && value !== null && value !== '';
    });
    
    // Check if all required fields are complete (for enabling submit button)
    const requiredFieldsComplete = completedMandatoryFields.length === requiredFields.length;
    
    // Show incomplete mandatory fields
    const incompleteFields = requiredFields.filter(field => {
      const value = values[field as keyof ProjectFormValues];
      return value === undefined || value === null || value === '';
    }).map(field => {
      const config = getFieldConfig(field);
      return config?.label || field;
    });

    // Calculate progress based on mandatory fields only
    const progress = requiredFields.length > 0 
      ? Math.round((completedMandatoryFields.length / requiredFields.length) * 100)
      : 100;

    return {
      progress,
      incompleteFields,
      canSubmit: requiredFieldsComplete,
    };
  };

  const [progress, setProgress] = useState(calculateProgress().progress);
  const [canSubmit, setCanSubmit] = useState(calculateProgress().canSubmit);
  const [localIncompleteFields, setLocalIncompleteFields] = useState<string[]>(calculateProgress().incompleteFields);

  // Update progress when form values change
  useEffect(() => {
    const subscription = form.watch(() => {
      const { progress: newProgress, incompleteFields, canSubmit: newCanSubmit } = calculateProgress();
      setProgress(newProgress);
      setCanSubmit(newCanSubmit);
      setLocalIncompleteFields(incompleteFields);
      onProgressChange?.(newProgress);
      onIncompleteFieldsChange?.(incompleteFields);
    });
    return () => subscription.unsubscribe();
  }, [form, onProgressChange, onIncompleteFieldsChange]);

  // Initial progress calculation
  useEffect(() => {
    const { progress: initialProgress, incompleteFields, canSubmit: initialCanSubmit } = calculateProgress();
    setProgress(initialProgress);
    setCanSubmit(initialCanSubmit);
    setLocalIncompleteFields(incompleteFields);
    onProgressChange?.(initialProgress);
    onIncompleteFieldsChange?.(incompleteFields);
  }, []);

  // Debug: Log when showCancelWarning changes
  useEffect(() => {
    console.log('showCancelWarning changed to:', showCancelWarning);
  }, [showCancelWarning]);
  
  // Watch for form changes to detect unsaved data
  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      // Check if any field has a non-empty value
      const hasData = Object.values(value).some(val => {
        if (val === null || val === undefined || val === '') return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      });
      
      console.log('Form changed - field:', name, 'type:', type, 'hasData:', hasData);
      // If we have initialData (restored form), always consider it as having changes
      // Otherwise, check if there's actual data in the form
      setHasFormChanges(!!initialData || hasData || customFields.length > 0);
    });
    
    return () => subscription.unsubscribe();
  }, [form, customFields.length, initialData]);

  // Auto-focus on project name field when modal opens
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      projectNameInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Auto-generate project code from project name when setting is enabled
  useEffect(() => {
    const projectCodeConfig = getFieldConfig('projectCode');
    if (projectCodeConfig?.autoGenerateCode) {
      const subscription = form.watch((value, { name }) => {
        if (name === 'projectName' && value.projectName) {
          const generatedCode = generateProjectCode(value.projectName);
          form.setValue('projectCode', generatedCode, { shouldValidate: false });
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [form, getFieldConfig, generateProjectCode]);

  // Update sections when showFullDetails changes in Quick Create mode
  useEffect(() => {
    if (quickCreateMode) {
      setSectionsOpen({
        general: showFullDetails,
        timeline: showFullDetails,
        management: showFullDetails,
        custom: showFullDetails,
      });
    }
  }, [showFullDetails, quickCreateMode]);

  // Two-way binding: When End Date changes, update Duration
  useEffect(() => {
    if (lastEditedField === 'endDate') {
      const startDate = form.getValues('startDate');
      const endDate = form.getValues('endDate');
      
      if (startDate && endDate && endDate > startDate) {
        const days = differenceInDays(endDate, startDate);
        form.setValue('duration', days, { shouldValidate: false });
      }
      setLastEditedField(null);
    }
  }, [lastEditedField, form]);

  // Two-way binding: When Duration changes, update End Date
  useEffect(() => {
    if (lastEditedField === 'duration') {
      const startDate = form.getValues('startDate');
      const duration = form.getValues('duration');
      
      if (startDate && duration && duration > 0) {
        const calculatedEndDate = addDays(startDate, duration);
        form.setValue('endDate', calculatedEndDate, { shouldValidate: false });
      }
      setLastEditedField(null);
    }
  }, [lastEditedField, form]);

  // In Quick Create mode, form can be submitted when minimum required fields are complete
  // In Legacy mode, form requires all mandatory fields (progress === 100)
  const isFormComplete = quickCreateMode ? canSubmit : progress === 100;

  const handleSubmit = (data: ProjectFormValues) => {
    onSubmit({ ...data, customFields });
  };

  // Helper function to get field label with dynamic asterisk
  const getFieldLabel = (fieldKey: string): string => {
    const config = getFieldConfig(fieldKey as any);
    if (!config) return fieldKey;
    return `${config.label}${config.isMandatory ? ' *' : ''}`;
  };

  const handleCancelClick = () => {
    console.log('Cancel clicked - hasFormChanges:', hasFormChanges, 'customFields:', customFields.length);
    
    if (hasFormChanges) {
      // Show warning dialog
      console.log('Showing warning dialog');
      setShowCancelWarning(true);
    } else {
      // No unsaved data, close immediately
      console.log('No unsaved data, closing immediately');
      onCancel();
    }
  };

  const handleDiscardChanges = () => {
    setShowCancelWarning(false);
    onCancel();
  };

  const handleSaveAsDraft = () => {
    setShowCancelWarning(false);
    if (onMinimize) {
      onMinimize();
    } else {
      // Fallback to cancel if minimize is not available
      onCancel();
    }
  };

  const addCustomField = () => {
    if (customFields.length >= 20) return;
    
    const newField: CustomField = {
      id: `custom_${Date.now()}`,
      fieldKey: '',
      fieldLabel: '',
      fieldType: 'text',
    };
    setCustomFields([...customFields, newField]);
  };

  const removeCustomField = (id: string) => {
    setCustomFields(customFields.filter(f => f.id !== id));
  };

  const updateCustomField = (id: string, updates: Partial<CustomField>) => {
    setCustomFields(customFields.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const toggleSection = (section: keyof typeof sectionsOpen) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Handle AI generation
  const handleAiGenerate = () => {
    setAiGenerating(true);
    
    // Simulate AI generation (replace with actual API call)
    setTimeout(() => {
      // Pre-fill form fields with AI-generated data with staggered animation
      const fields = [
        { name: 'projectName', value: aiProjectDescription.split(' ').slice(0, 4).join(' ') || 'AI Generated Project', delay: 0 },
        { name: 'projectCode', value: 'AI-' + Date.now().toString().slice(-6), delay: 100 },
        { name: 'description', value: aiProjectDescription, delay: 200 },
        { name: 'status', value: 'not-started', delay: 300 },
        { name: 'priority', value: 'medium', delay: 400 },
        { name: 'type', value: 'internal', delay: 500 },
      ];

      fields.forEach(({ name, value, delay }) => {
        setTimeout(() => {
          form.setValue(name as any, value);
        }, delay);
      });

      // Set dates with delay
      setTimeout(() => {
        const startDate = new Date();
        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 6); // Default 6 months
        form.setValue('startDate', startDate);
        form.setValue('endDate', endDate);
        form.setValue('projectManager', 'john-smith');
      }, 600);
      
      setTimeout(() => {
        setAiGenerating(false);
        setShowAiPanel(false);
        
        // Show full details after AI generation
        if (onShowFullDetails) {
          onShowFullDetails();
        }
      }, 1000);
    }, 1500);
  };

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Esc to cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
      // Ctrl/Cmd + Enter to submit (if form is complete)
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && isFormComplete) {
        e.preventDefault();
        form.handleSubmit(handleSubmit)();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFormComplete, onCancel, form]);

  // Handle field highlighting and scrolling
  useEffect(() => {
    console.log('highlightedField changed to:', highlightedField);
    console.log('Available field refs:', Object.keys(fieldRefs.current));
    
    if (highlightedField && fieldRefs.current[highlightedField]) {
      const fieldElement = fieldRefs.current[highlightedField];
      console.log('Field element found:', fieldElement);
      
      if (fieldElement) {
        // Scroll to the field with smooth behavior
        fieldElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
        console.log('Scrolled to field:', highlightedField);
        
        // Focus the actual input/textarea/select element inside
        const inputElement = fieldElement.querySelector('input, textarea, select') as HTMLElement;
        if (inputElement) {
          // Small delay to ensure scroll completes before focusing
          setTimeout(() => {
            inputElement.focus();
            console.log('Focused input element');
          }, 300);
        }
      }
    } else if (highlightedField) {
      console.log('Field ref not found for:', highlightedField);
    }
  }, [highlightedField]);

  // Helper function to wrap field with ref and highlight styling
  const wrapFieldWithHighlight = (fieldKey: string, fieldContent: React.ReactNode) => {
    return (
      <div 
        key={fieldKey}
        ref={(el) => { fieldRefs.current[fieldKey] = el; }}
        className={cn(
          "transition-all duration-300 rounded-md",
          highlightedField === fieldKey && "ring-2 ring-red-500 ring-offset-2 p-2 -m-2 animate-pulse-scale"
        )}
      >
        {fieldContent}
      </div>
    );
  };

  // Helper function to render a field based on its configuration
  const renderField = (fieldKey: string) => {
    const config = getFieldConfig(fieldKey as any);
    if (!config) return null;

    const isMandatory = config.isMandatory;
    const label = `${config.label}${isMandatory ? ' *' : ''}`;

    switch (fieldKey) {
      case 'projectName':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="projectName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter project name" 
                    className="text-lg font-medium"
                    autoComplete="off"
                    aria-label="Project name"
                    aria-required={isMandatory}
                    {...field}
                    ref={(e) => {
                      field.ref(e);
                      if (quickCreateMode) {
                        projectNameInputRef.current = e;
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'projectCode':
        const projectCodeConfig = getFieldConfig('projectCode');
        const isAutoGenerated = projectCodeConfig?.autoGenerateCode || false;
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="projectCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Input 
                    placeholder={isAutoGenerated ? "Auto-generated from project name" : "e.g., PRJ-2024-001"} 
                    disabled={isAutoGenerated}
                    {...field} 
                  />
                </FormControl>
                {isAutoGenerated && (
                  <FormDescription className="text-xs">
                    This code is automatically generated from the project name
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'status':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Combobox
                    options={statusOptions.map(option => ({ 
                      value: option.toLowerCase().replace(' ', '-'), 
                      label: option 
                    }))}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select status"
                    searchPlaceholder="Search status..."
                    emptyMessage="No status found."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'priority':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Combobox
                    options={priorityOptions.map(option => ({ 
                      value: option.toLowerCase(), 
                      label: option 
                    }))}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select priority"
                    searchPlaceholder="Search priority..."
                    emptyMessage="No priority found."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'type':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Combobox
                    options={typeOptions.map(option => ({ 
                      value: option.toLowerCase().replace(' ', '-'), 
                      label: option 
                    }))}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select type"
                    searchPlaceholder="Search type..."
                    emptyMessage="No type found."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'startDate':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{label}</FormLabel>
                <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="flex flex-col">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={(date) => {
                          field.onChange(date);
                          setStartDateOpen(false);
                          // In Duration mode: Recalculate end date when start date changes
                          if (!isEndDateMode) {
                            const duration = form.getValues('duration');
                            const durationUnit = form.getValues('durationUnit') || 'days';
                            if (date && duration && duration > 0) {
                              const calculatedEndDate = addDurationToDate(date, duration, durationUnit);
                              form.setValue('endDate', calculatedEndDate, { shouldValidate: false });
                            }
                          }
                          // In End Date mode: Recalculate duration when start date changes
                          else {
                            const endDate = form.getValues('endDate');
                            if (date && endDate) {
                              const durationUnit = form.getValues('durationUnit') || 'days';
                              const availableUnits = getAvailableUnits(date, endDate);
                              
                              if (availableUnits.includes(durationUnit)) {
                                const durationValue = calculateDurationInUnit(date, endDate, durationUnit);
                                form.setValue('duration', durationValue);
                              } else {
                                const newUnit = availableUnits.length > 0 ? availableUnits[0] : 'days';
                                const durationValue = calculateDurationInUnit(date, endDate, newUnit);
                                form.setValue('durationUnit', newUnit);
                                form.setValue('duration', durationValue);
                              }
                            }
                          }
                        }}
                        disabled={(date) => date < new Date("1900-01-01")}
                        captionLayout="dropdown"
                        fromYear={1900}
                        toYear={2100}
                        initialFocus
                      />
                      <div className="border-t border-border p-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            const today = new Date();
                            field.onChange(today);
                            setStartDateOpen(false);
                            // In Duration mode: Recalculate end date
                            if (!isEndDateMode) {
                              const duration = form.getValues('duration');
                              const durationUnit = form.getValues('durationUnit') || 'days';
                              if (duration && duration > 0) {
                                const calculatedEndDate = addDurationToDate(today, duration, durationUnit);
                                form.setValue('endDate', calculatedEndDate, { shouldValidate: false });
                              }
                            }
                            // In End Date mode: Recalculate duration
                            else {
                              const endDate = form.getValues('endDate');
                              if (endDate) {
                                const durationUnit = form.getValues('durationUnit') || 'days';
                                const availableUnits = getAvailableUnits(today, endDate);
                                
                                if (availableUnits.includes(durationUnit)) {
                                  const durationValue = calculateDurationInUnit(today, endDate, durationUnit);
                                  form.setValue('duration', durationValue);
                                } else {
                                  const newUnit = availableUnits.length > 0 ? availableUnits[0] : 'days';
                                  const durationValue = calculateDurationInUnit(today, endDate, newUnit);
                                  form.setValue('durationUnit', newUnit);
                                  form.setValue('duration', durationValue);
                                }
                              }
                            }
                          }}
                        >
                          Today
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'endDate':
        const startDate = form.watch('startDate');
        const isEndDateDisabled = !startDate || !isEndDateMode;
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{label}</FormLabel>
                <Popover 
                  open={endDateOpen} 
                  onOpenChange={(open) => {
                    setEndDateOpen(open);
                    if (open) {
                      // When opening, set the calendar month to the selected date, start date, or today
                      setEndDateMonth(field.value || startDate || new Date());
                    }
                  }}
                >
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        disabled={isEndDateDisabled}
                        className={cn(
                          "w-full pl-3 text-left font-normal transition-all",
                          !field.value && "text-muted-foreground",
                          isEndDateDisabled && "opacity-50 cursor-not-allowed bg-muted"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>
                            {!startDate ? "Set start date first" : !isEndDateMode ? "Calculated from duration" : "Pick a date"}
                          </span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="flex flex-col">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        month={endDateMonth}
                        onMonthChange={setEndDateMonth}
                        onSelect={(date) => {
                          field.onChange(date);
                          setEndDateOpen(false);
                          // In End Date mode: Update duration (read-only) when end date changes
                          if (date && startDate && isEndDateMode) {
                            const currentUnit = form.watch('durationUnit') || 'days';
                            const availableUnits = getAvailableUnits(startDate, date);
                            
                            // If current unit is available, use it
                            if (availableUnits.includes(currentUnit)) {
                              const durationValue = calculateDurationInUnit(startDate, date, currentUnit);
                              form.setValue('duration', durationValue);
                            } else {
                              // Auto-switch to the first available unit (or days as fallback)
                              const newUnit = availableUnits.length > 0 ? availableUnits[0] : 'days';
                              const durationValue = calculateDurationInUnit(startDate, date, newUnit);
                              form.setValue('durationUnit', newUnit);
                              form.setValue('duration', durationValue);
                            }
                          }
                        }}
                        disabled={(date) => {
                          // Disable dates before 1900
                          if (date < new Date("1900-01-01")) return true;
                          // Disable dates on or before start date
                          if (startDate) {
                            const nextDay = new Date(startDate);
                            nextDay.setDate(nextDay.getDate() + 1);
                            nextDay.setHours(0, 0, 0, 0);
                            return date < nextDay;
                          }
                          return false;
                        }}
                        captionLayout="dropdown"
                        fromYear={startDate?.getFullYear() || 1900}
                        toYear={2100}
                        fromMonth={startDate || undefined}
                        initialFocus
                      />
                      <div className="border-t border-border p-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            const today = new Date();
                            // Only allow today if it's after start date
                            if (startDate) {
                              const nextDay = new Date(startDate);
                              nextDay.setDate(nextDay.getDate() + 1);
                              nextDay.setHours(0, 0, 0, 0);
                              if (today >= nextDay) {
                                field.onChange(today);
                                setEndDateOpen(false);
                                // In End Date mode: Update duration (read-only) when end date changes
                                if (isEndDateMode) {
                                  const currentUnit = form.watch('durationUnit') || 'days';
                                  const availableUnits = getAvailableUnits(startDate, today);
                                  
                                  // If current unit is available, use it
                                  if (availableUnits.includes(currentUnit)) {
                                    const durationValue = calculateDurationInUnit(startDate, today, currentUnit);
                                    form.setValue('duration', durationValue);
                                  } else {
                                    // Auto-switch to the first available unit (or days as fallback)
                                    const newUnit = availableUnits.length > 0 ? availableUnits[0] : 'days';
                                    const durationValue = calculateDurationInUnit(startDate, today, newUnit);
                                    form.setValue('durationUnit', newUnit);
                                    form.setValue('duration', durationValue);
                                  }
                                }
                              }
                            }
                          }}
                          disabled={!startDate || new Date() <= startDate}
                        >
                          Today
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'duration':
        const startDateForDuration = form.watch('startDate');
        const endDateForDuration = form.watch('endDate');
        const currentDuration = form.watch('duration');
        const currentDurationUnit = form.watch('durationUnit') || 'days';
        const isDurationDisabled = !startDateForDuration || isEndDateMode;
        
        // Get available units based on current end date (for End Date mode)
        const availableUnitsForSelect = isEndDateMode && startDateForDuration && endDateForDuration
          ? getAvailableUnits(startDateForDuration, endDateForDuration)
          : ['days', 'weeks', 'months', 'years'];
        
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="duration"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <div className="flex gap-2 items-end">
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Duration
                    </label>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        max={getMaxDuration(currentDurationUnit)}
                        disabled={isDurationDisabled}
                        placeholder={
                          !startDateForDuration 
                            ? "Set start date first" 
                            : isEndDateMode 
                              ? "Calculated from end date" 
                              : "Enter duration"
                        }
                        className={cn(
                          "transition-all",
                          isDurationDisabled && "opacity-50 cursor-not-allowed bg-muted"
                        )}
                        value={field.value || ''}
                        onChange={(e) => {
                          let value = e.target.value === '' ? undefined : parseInt(e.target.value);
                          const maxValue = getMaxDuration(currentDurationUnit);
                          
                          // Cap the value if it exceeds the max
                          if (value && value > maxValue) {
                            value = maxValue;
                            setShowDurationCapWarning(true);
                            setTimeout(() => setShowDurationCapWarning(false), 3000);
                          }
                          
                          field.onChange(value);
                          // In Duration mode: Update end date when duration changes
                          if (value && value > 0 && startDateForDuration && !isEndDateMode) {
                            const newEndDate = addDurationToDate(startDateForDuration, value, currentDurationUnit);
                            form.setValue('endDate', newEndDate);
                          }
                        }}
                      />
                    </FormControl>
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Unit
                    </label>
                    <Select
                    disabled={!startDateForDuration}
                    value={currentDurationUnit}
                    onValueChange={(value: DurationUnit) => {
                      form.setValue('durationUnit', value);
                      
                      if (isEndDateMode) {
                        // In End Date mode: Recalculate duration in new unit
                        if (startDateForDuration && endDateForDuration) {
                          const newDurationValue = calculateDurationInUnit(startDateForDuration, endDateForDuration, value);
                          form.setValue('duration', newDurationValue);
                        }
                      } else {
                        // In Duration mode: Cap duration if it exceeds max for new unit, then recalculate end date
                        if (currentDuration && currentDuration > 0 && startDateForDuration) {
                          const maxValue = getMaxDuration(value);
                          let cappedDuration = currentDuration;
                          
                          if (currentDuration > maxValue) {
                            cappedDuration = maxValue;
                            form.setValue('duration', cappedDuration);
                            setShowDurationCapWarning(true);
                            setTimeout(() => setShowDurationCapWarning(false), 3000);
                          }
                          
                          const newEndDate = addDurationToDate(startDateForDuration, cappedDuration, value);
                          form.setValue('endDate', newEndDate);
                        }
                      }
                    }}
                  >
                    <SelectTrigger className={cn(
                      "w-full transition-all",
                      !startDateForDuration && "opacity-50 cursor-not-allowed"
                    )}>
                      <SelectValue>
                        {currentDuration && currentDuration > 0 
                          ? formatDurationUnit(currentDuration, currentDurationUnit)
                          : currentDurationUnit
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {availableUnitsForSelect.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit.charAt(0).toUpperCase() + unit.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  </div>
                </div>
                {showDurationCapWarning && (
                  <p className="text-xs text-muted-foreground animate-in fade-in slide-in-from-top-1 duration-300">
                    Duration capped at {getMaxDuration(currentDurationUnit)} {formatDurationUnit(getMaxDuration(currentDurationUnit), currentDurationUnit)} (maximum allowed)
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'projectManager':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="projectManager"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Combobox
                    options={
                      fetchedEmployees.length > 0
                        ? fetchedEmployees.map(employee => ({ 
                            value: employee.id, 
                            label: employee.name 
                          }))
                        : projectManagerOptions.map(option => ({ 
                            value: option.toLowerCase().replace(' ', '-'), 
                            label: option 
                          }))
                    }
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder={isLoadingEmployees ? "Loading employees..." : "Select project manager"}
                    searchPlaceholder="Search project manager..."
                    emptyMessage="No project manager found."
                    disabled={isLoadingEmployees}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'clients':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="clients"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <MultiSelectWithFavorites
                    options={
                      fetchedClients.length > 0
                        ? fetchedClients.map(client => ({ 
                            value: client.id, 
                            label: client.name 
                          }))
                        : clientOptions.map(client => ({ 
                            value: client.toLowerCase().replace(/\s+/g, '-'), 
                            label: client 
                          }))
                    }
                    placeholder={isLoadingClients ? "Loading clients..." : "Select clients..."}
                    emptyMessage="No clients found."
                    value={field.value || []}
                    onValueChange={field.onChange}
                    disabled={isLoadingClients}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'description':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <TextareaWithCount 
                    placeholder="Provide a detailed description of the project..."
                    className="min-h-24"
                    maxLength={500}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'budget':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="budget"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    placeholder="Enter budget amount" 
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Optional: Enter the project budget in your local currency
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'tags':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <MultiSelect
                    options={tagOptions.map(tag => ({ value: tag.toLowerCase(), label: tag }))}
                    placeholder="Select tags..."
                    emptyMessage="No tags found."
                    value={field.value || []}
                    onValueChange={field.onChange}
                  />
                </FormControl>
                <FormDescription>
                  Add tags to categorize and organize this project
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Template Selection - Always shown first in Quick Create mode */}
          {quickCreateMode && templates.length > 0 && onTemplateSelect && (
            <div className="rounded-lg border bg-card p-4 space-y-3">
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5 cursor-help">
                        <Label className="text-sm font-medium">Start from template</Label>
                        <Info className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-xs">
                        Templates pre-fill project fields with common settings. You can customize any field after selection.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-2">
                <Combobox
                  options={[
                    { value: 'none', label: 'None (blank project)' },
                    ...templates.map(template => ({
                      value: template.id,
                      label: template.name,
                      description: template.description,
                      isFavorite: template.isFavorite
                    }))
                  ]}
                  value={selectedTemplate || 'none'}
                  onValueChange={(value) => {
                    onTemplateSelect(value);
                    // Show a subtle animation when template is selected
                    if (value !== 'none' && value !== '') {
                      // Template selected - could trigger animation
                    }
                  }}
                  placeholder="Select template"
                  searchPlaceholder="Search templates..."
                  emptyMessage="No template found."
                  className={cn(
                    "w-full transition-all",
                    selectedTemplate && selectedTemplate !== 'none' && "border-primary/50 bg-primary/5"
                  )}
                  renderOption={(option) => (
                    <div className="flex items-start gap-2 w-full">
                      {option.isFavorite && (
                        <Star className="h-3 w-3 fill-amber-500 text-amber-500 shrink-0 mt-0.5" />
                      )}
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="truncate">{option.label}</span>
                        {option.description && (
                          <span className="text-xs text-muted-foreground line-clamp-2">{option.description}</span>
                        )}
                      </div>
                    </div>
                  )}
                />
              </div>
              {selectedTemplate && selectedTemplate !== 'none' && (
                <div className="rounded-md bg-primary/5 border border-primary/20 p-3 space-y-1 animate-in slide-in-from-top-1 duration-200">
                  <p className="text-xs font-medium text-primary">
                    ✓ Template selected: {templates.find(t => t.id === selectedTemplate)?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {templates.find(t => t.id === selectedTemplate)?.description}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Quick Create Section - Only shown in Quick Create mode when NOT showing full details */}
          {quickCreateMode && !showFullDetails && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-primary/5 border-primary/20 p-4 space-y-4 animate-in fade-in-0 duration-300">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    Quick Create
                    <Badge variant="secondary" className="text-xs font-normal">Fast Track</Badge>
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Complete the required fields below to create your project quickly.
                  </p>
                </div>

                {/* Dynamically render all mandatory fields */}
                <div className="space-y-4">
                  {mandatoryFields.map((fieldKey) => (
                    <div key={fieldKey}>
                      {renderField(fieldKey)}
                    </div>
                  ))}
                </div>
                
                {/* Progress indicator for Quick Create */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className={cn(
                      "font-medium transition-colors",
                      progress === 100 ? "text-success" : canSubmit ? "text-primary" : "text-muted-foreground"
                    )}>
                      {progress === 100 
                        ? "✓ All fields complete!" 
                        : canSubmit 
                          ? "✓ All required fields complete! Ready to create."
                          : `Complete ${localIncompleteFields.length} required field${localIncompleteFields.length !== 1 ? 's' : ''} to continue`}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{progress}%</span>
                      <Progress value={progress} className="w-24 h-2" />
                    </div>
                  </div>
                  {localIncompleteFields.length > 0 && !canSubmit && (
                    <p className="text-xs text-muted-foreground">
                      Missing: {localIncompleteFields.slice(0, 3).join(', ')}
                      {localIncompleteFields.length > 3 && ` +${localIncompleteFields.length - 3} more`}
                    </p>
                  )}
                </div>
              </div>

              {/* Progressive Disclosure Card - Only shown when mandatory fields are complete */}
              {canSubmit && (
                <div className="rounded-lg border bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 p-6 space-y-4 animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 mt-0.5">
                      <Briefcase className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div>
                        <h4 className="text-base font-semibold text-foreground mb-1">
                          Need More Control?
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Add optional details to unlock:
                        </p>
                      </div>
                      
                      <ul className="space-y-2 text-sm text-foreground">
                        <li className="flex items-start gap-2">
                          <span className="text-primary mt-0.5">•</span>
                          <span>Budget tracking & cost management</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-primary mt-0.5">•</span>
                          <span>Resource allocation & team planning</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-primary mt-0.5">•</span>
                          <span>Advanced scheduling & milestones</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-primary mt-0.5">•</span>
                          <span>Custom fields & metadata</span>
                        </li>
                      </ul>

                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2">
                        <Button
                          type="button"
                          onClick={onShowFullDetails}
                          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
                        >
                          Add Optional Details
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          or continue with Quick Create
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* General Info Section - Hidden in Quick Create mode unless showFullDetails is true */}
          {(!quickCreateMode || showFullDetails) && (
          <Collapsible open={sectionsOpen.general} onOpenChange={() => toggleSection('general')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors">
                <h3 className="text-lg font-semibold">General Information</h3>
                <ChevronDown className={cn(
                  "h-5 w-5 transition-transform",
                  sectionsOpen.general && "rotate-180"
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="projectName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{getFieldLabel('projectName')}</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter project name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="projectCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{getFieldLabel('projectCode')}</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., PRJ-2024-001" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel>{getFieldLabel('description')}</FormLabel>
                            {quickCreateMode && !showAiPanel && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowAiPanel(true)}
                                className="h-7 text-xs text-primary hover:text-primary/80"
                              >
                                <Sparkles className="h-3 w-3 mr-1" />
                                Generate with AI
                              </Button>
                            )}
                          </div>
                          <FormControl>
                            <TextareaWithCount 
                              placeholder="Provide a detailed description of the project..."
                              className="min-h-24"
                              maxLength={500}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Inline AI Panel */}
                    {showAiPanel && (
                      <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4 space-y-4 animate-in slide-in-from-top-2 duration-300">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-primary" />
                            <div>
                              <h4 className="font-semibold text-sm">AI Project Assistant</h4>
                              <p className="text-xs text-muted-foreground">
                                Describe your project and I'll help fill in the details
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowAiPanel(false)}
                            className="h-7 w-7 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="ai-description" className="text-sm font-medium">
                              What are you building? *
                            </Label>
                            <Textarea
                              id="ai-description"
                              placeholder="e.g., A customer portal with authentication, dashboard, and reporting features..."
                              value={aiProjectDescription}
                              onChange={(e) => setAiProjectDescription(e.target.value)}
                              className="min-h-20 resize-none bg-background"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label htmlFor="ai-objectives" className="text-sm font-medium">
                                Key Objectives
                              </Label>
                              <Input
                                id="ai-objectives"
                                placeholder="e.g., Improve customer satisfaction..."
                                value={aiObjectives}
                                onChange={(e) => setAiObjectives(e.target.value)}
                                className="bg-background"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="ai-timeline" className="text-sm font-medium">
                                Timeline
                              </Label>
                              <Input
                                id="ai-timeline"
                                placeholder="e.g., 6 months, Q2 2024..."
                                value={aiTimeline}
                                onChange={(e) => setAiTimeline(e.target.value)}
                                className="bg-background"
                              />
                            </div>
                          </div>

                          <Button
                            type="button"
                            onClick={handleAiGenerate}
                            disabled={!aiProjectDescription.trim() || aiGenerating}
                            className="w-full"
                          >
                            {aiGenerating ? (
                              <>
                                <Sparkles className="h-4 w-4 mr-2 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4 mr-2" />
                                Generate Project Details
                              </>
                            )}
                          </Button>
                        </div>

                        <div className="rounded-md bg-background/50 p-3 text-xs text-muted-foreground">
                          <p className="font-medium mb-1">AI will help you with:</p>
                          <ul className="space-y-0.5 list-disc list-inside">
                            <li>Project name and code generation</li>
                            <li>Detailed description and objectives</li>
                            <li>Timeline and milestone suggestions</li>
                            <li>Resource allocation recommendations</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{getFieldLabel('status')}</FormLabel>
                          <FormControl>
                            <Combobox
                              options={statusOptions.map(option => ({ 
                                value: option.toLowerCase().replace(' ', '-'), 
                                label: option 
                              }))}
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder="Select status"
                              searchPlaceholder="Search status..."
                              emptyMessage="No status found."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="priority"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{getFieldLabel('priority')}</FormLabel>
                          <FormControl>
                            <Combobox
                              options={priorityOptions.map(option => ({ 
                                value: option.toLowerCase(), 
                                label: option 
                              }))}
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder="Select priority"
                              searchPlaceholder="Search priority..."
                              emptyMessage="No priority found."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{getFieldLabel('type')}</FormLabel>
                          <FormControl>
                            <Combobox
                              options={typeOptions.map(option => ({ 
                                value: option.toLowerCase().replace(' ', '-'), 
                                label: option 
                              }))}
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder="Select type"
                              searchPlaceholder="Search type..."
                              emptyMessage="No type found."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          {/* Timeline Section - Hidden in Quick Create mode unless showFullDetails is true */}
          {(!quickCreateMode || showFullDetails) && (
          <Collapsible open={sectionsOpen.timeline} onOpenChange={() => toggleSection('timeline')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors">
                <h3 className="text-lg font-semibold">Timeline</h3>
                <div className="flex items-center gap-4">
                  {timelineSettings.allowModeSwitch && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <span className="text-sm text-muted-foreground">
                              {isEndDateMode ? 'End Date' : 'Duration'}
                            </span>
                            <Switch
                              checked={!isEndDateMode}
                              onCheckedChange={(checked) => setIsEndDateMode(!checked)}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="left" align="center" className="max-w-[200px]">
                          <p>Switch between entering the End Date or a Duration</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {!timelineSettings.allowModeSwitch && (
                    <span className="text-sm text-muted-foreground">
                      {isEndDateMode ? 'End Date' : 'Duration'}
                    </span>
                  )}
                  <ChevronDown className={cn(
                    "h-5 w-5 transition-transform",
                    sectionsOpen.timeline && "rotate-180"
                  )} />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {renderField('startDate')}
                    {renderField('endDate')}
                    {renderField('duration')}
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          {/* Management Section - Hidden in Quick Create mode unless showFullDetails is true */}
          {(!quickCreateMode || showFullDetails) && (
          <Collapsible open={sectionsOpen.management} onOpenChange={() => toggleSection('management')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors">
                <h3 className="text-lg font-semibold">Management</h3>
                <ChevronDown className={cn(
                  "h-5 w-5 transition-transform",
                  sectionsOpen.management && "rotate-180"
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <FormField
                    control={form.control}
                    name="projectManager"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{getFieldLabel('projectManager')}</FormLabel>
                        <FormControl>
                          <Combobox
                            options={
                              fetchedEmployees.length > 0
                                ? fetchedEmployees.map(employee => ({ 
                                    value: employee.id, 
                                    label: employee.name 
                                  }))
                                : projectManagerOptions.map(option => ({ 
                                    value: option.toLowerCase().replace(' ', '-'), 
                                    label: option 
                                  }))
                            }
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder={isLoadingEmployees ? "Loading employees..." : "Select project manager"}
                            searchPlaceholder="Search project manager..."
                            emptyMessage="No project manager found."
                            disabled={isLoadingEmployees}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="budget"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{getFieldLabel('budget')}</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            placeholder="Enter budget amount" 
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Optional: Enter the project budget in your local currency
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="clients"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{getFieldLabel('clients')}</FormLabel>
                        <FormControl>
                          <MultiSelect
                            options={
                              fetchedClients.length > 0
                                ? fetchedClients.map(client => ({ 
                                    value: client.id, 
                                    label: client.name 
                                  }))
                                : clientOptions.map(client => ({ 
                                    value: client.toLowerCase().replace(/\s+/g, '-'), 
                                    label: client 
                                  }))
                            }
                            placeholder={isLoadingClients ? "Loading clients..." : "Select clients..."}
                            emptyMessage="No clients found."
                            value={field.value || []}
                            onValueChange={field.onChange}
                          />
                        </FormControl>
                        <FormDescription>
                          Select one or more clients associated with this project
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{getFieldLabel('tags')}</FormLabel>
                        <FormControl>
                          <MultiSelect
                            options={tagOptions.map(tag => ({ value: tag.toLowerCase(), label: tag }))}
                            placeholder="Select tags..."
                            emptyMessage="No tags found."
                            value={field.value || []}
                            onValueChange={field.onChange}
                          />
                        </FormControl>
                        <FormDescription>
                          Add tags to categorize and organize this project
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          {/* Custom Fields Section - Hidden in Quick Create mode unless showFullDetails is true */}
          {(!quickCreateMode || showFullDetails) && (
          <Collapsible open={sectionsOpen.custom} onOpenChange={() => toggleSection('custom')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Custom Fields</h3>
                  <Badge variant="secondary">{customFields.length}/20</Badge>
                </div>
                <ChevronDown className={cn(
                  "h-5 w-5 transition-transform",
                  sectionsOpen.custom && "rotate-180"
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  {customFields.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <p className="mb-4">No custom fields added yet</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addCustomField}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Custom Field
                      </Button>
                    </div>
                  ) : (
                    <>
                      {customFields.map((field, index) => (
                        <div key={field.id} className="rounded-lg border p-4 space-y-3 bg-muted/30">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Custom Field {index + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeCustomField(field.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Field Key</label>
                              <Input
                                placeholder="field_name"
                                value={field.fieldKey}
                                onChange={(e) => updateCustomField(field.id, { fieldKey: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Field Label</label>
                              <Input
                                placeholder="Display Name"
                                value={field.fieldLabel}
                                onChange={(e) => updateCustomField(field.id, { fieldLabel: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Field Type</label>
                            <Select
                              value={field.fieldType}
                              onValueChange={(value) => updateCustomField(field.id, { fieldType: value as any })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="text">Text</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                                <SelectItem value="date">Date</SelectItem>
                                <SelectItem value="boolean">Boolean</SelectItem>
                                <SelectItem value="dropdown">Dropdown</SelectItem>
                                <SelectItem value="multiselect">Multi-select</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                      {customFields.length < 20 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addCustomField}
                          className="w-full"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Another Custom Field
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          {/* Form Actions */}
          <div className="flex gap-3 pt-4 border-t sticky bottom-0 bg-background pb-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancelClick}
              className="flex-1"
              aria-label="Cancel project creation (Esc)"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isFormComplete}
              className={cn(
                "flex-1 relative transition-all",
                isFormComplete && "shadow-md hover:shadow-lg"
              )}
              aria-label={isFormComplete ? "Create project (Ctrl+Enter)" : "Complete required fields to create project"}
            >
              {quickCreateMode && canSubmit ? 'Create Project' : 'Save Project'}
              {isFormComplete && (
                <span className="ml-2 text-xs opacity-60">⌘↵</span>
              )}
            </Button>
          </div>
        </form>
      </Form>

      {/* Unsaved Changes Warning Dialog */}
      <AlertDialog open={showCancelWarning} onOpenChange={setShowCancelWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/20">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500" />
              </div>
              <div className="flex-1">
                <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                <AlertDialogDescription>
                  You have unsaved changes in this form. What would you like to do?
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-col gap-2 sm:gap-2">
            <AlertDialogCancel 
              onClick={() => setShowCancelWarning(false)}
              className="w-full m-0"
            >
              Go Back to Form
            </AlertDialogCancel>
            <div className="flex gap-2 w-full">
              <AlertDialogAction
                onClick={handleSaveAsDraft}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                Save as Draft
              </AlertDialogAction>
              <AlertDialogAction
                onClick={handleDiscardChanges}
                className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              >
                Discard Changes
              </AlertDialogAction>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
